import { FetchResult, MutationFunctionOptions, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import {
  downloadFileByUrl,
  evictCache,
  GET_AND_SAVE_ASC_REPORT_SIGNED_URL,
  GetAndSaveAscReportSignedUrlMutation,
  GetAndSaveAscReportSignedUrlMutationVariables,
  PeriodName,
} from '@neo.tax/shared';

export const useGetAndSaveAscReportSignedUrl = (): {
  getAndSaveAscReportSignedUrl: (
    options?: MutationFunctionOptions<
      GetAndSaveAscReportSignedUrlMutation,
      GetAndSaveAscReportSignedUrlMutationVariables
    >,
  ) => Promise<FetchResult<GetAndSaveAscReportSignedUrlMutation>>;
  loading: boolean;
  called: boolean;
  downloadAscProjectsReport: (params: { fiscalYearId: string; periodName: PeriodName }) => Promise<void>;
} => {
  const [getAndSaveAscReportSignedUrl, { called, loading }] = useMutation<
    GetAndSaveAscReportSignedUrlMutation,
    GetAndSaveAscReportSignedUrlMutationVariables
  >(GET_AND_SAVE_ASC_REPORT_SIGNED_URL, {
    update: (cache) => {
      evictCache(cache, ['getBackgroundOperations']);
    },
  });

  const downloadAscProjectsReport = useCallback(
    async ({ fiscalYearId, periodName }: { fiscalYearId: string; periodName: PeriodName }): Promise<void> => {
      const { data } = await getAndSaveAscReportSignedUrl({ variables: { fiscalYearId, periodName } });
      const signedUrl = data?.getAndSaveAscReportSignedUrl.signedUrl;
      if (!signedUrl) {
        return;
      }
      // The file name here is irrelevant. The file will be downloaded as the original name uploaded to GCP Cloud Storage.
      downloadFileByUrl(signedUrl, 'asc-projects-report.xlsx');
    },
    [getAndSaveAscReportSignedUrl],
  );

  return {
    getAndSaveAscReportSignedUrl,
    loading,
    called,
    downloadAscProjectsReport,
  };
};
